import { useEffect, useRef, useState } from 'react';
import { Edit3, Type } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SignaturePad, { type SignaturePadRef } from '@/components/ui/SignaturePad';
import { nextSigningNameInput, signingNameInputValue, typedSignatureDataUrl } from '@/lib/contract-signing';

type Mode = 'draw' | 'type';

export function ContractSignaturePad({
  partyName,
  value,
  onChange,
  disabled = false,
  printedName,
  onPrintedNameChange,
  printedNamePlaceholder = 'Printed name',
  printedNameLabel = 'Printed name',
  waitNote,
  width = 320,
  height = 88,
}: {
  partyName: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  printedName?: string;
  onPrintedNameChange?: (next: string) => void;
  printedNamePlaceholder?: string;
  printedNameLabel?: string;
  waitNote?: string | null;
  width?: number;
  height?: number;
}) {
  const padRef = useRef<SignaturePadRef>(null);
  const [mode, setMode] = useState<Mode>('draw');
  const [typed, setTyped] = useState(printedName || partyName);

  useEffect(() => {
    const next = (printedName || partyName).trim();
    if (next) setTyped(next);
  }, [partyName, printedName]);

  if (waitNote) {
    return (
      <p className="cd-sig-wait" data-testid="contract-signature-wait">
        {waitNote}
      </p>
    );
  }

  const applyTyped = (name: string) => {
    const next = name.trim();
    if (!next) {
      onChange('');
      return;
    }
    onChange(typedSignatureDataUrl(next, width, height));
  };

  return (
    <div className="cd-sig-capture" data-testid="contract-signature-pad">
      {onPrintedNameChange ? (
        <label className="cd-sig-name-field">
          <span>Printed name</span>
          <Input
            value={signingNameInputValue(printedName)}
            disabled={disabled}
            onChange={(event) => {
              const next = nextSigningNameInput(
                printedName ?? '',
                event.target.value,
                printedNamePlaceholder,
              );
              onPrintedNameChange(next);
              if (mode === 'type') applyTyped(next);
            }}
            placeholder={printedNamePlaceholder}
            aria-label={printedNameLabel}
            className="cd-sign-name-input text-card-foreground caret-[hsl(var(--card-foreground))]"
            autoComplete="name"
          />
        </label>
      ) : null}
      <div className="cd-sig-tools">
        <Button
          type="button"
          size="sm"
          variant={mode === 'draw' ? 'default' : 'outline'}
          disabled={disabled}
          onClick={() => {
            setMode('draw');
            onChange('');
            padRef.current?.clear();
          }}
        >
          <Edit3 className="h-3.5 w-3.5 mr-1" />
          Draw
        </Button>
        <Button
          type="button"
          size="sm"
          variant={mode === 'type' ? 'default' : 'outline'}
          disabled={disabled}
          onClick={() => {
            setMode('type');
            applyTyped(typed || printedName || partyName);
          }}
        >
          <Type className="h-3.5 w-3.5 mr-1" />
          Type
        </Button>
      </div>
      {mode === 'draw' ? (
        <div className="cd-sig-pad">
          <SignaturePad
            ref={padRef}
            value={value}
            onChange={onChange}
            width={width}
            height={height}
            disabled={disabled}
            fluid
            compact
          />
        </div>
      ) : (
        <div className="cd-typed-preview" aria-hidden="true">
          {typed.trim() || 'Signature preview'}
        </div>
      )}
    </div>
  );
}
